import React, { useEffect, useMemo, useState } from "react";
import posthog from "posthog-js";
import axios from "axios";
import AppHeader from "@/components/AppHeader/AppHeader";
import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import ProjectPage from "@/components/ProjectList/ProjectPage";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { resetCodebaseState } from "@/store/codebaseSlice";
import { resetGithubState } from "@/store/gitStatusSlice";
import { useAiActionStore } from "@/store/zustand/aiActionStore";
import { useAiContextStore } from "@/store/zustand/aiContextStore";
import useDesignSystemStore from "@/store/zustand/aiDesignSystemBuilder";
import { useUser, useSession } from "@clerk/clerk-react";
import { useCanvasStore } from "@/store/zustand/canvasStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useDevServerStore } from "@/store/zustand/devServerStore";
import useLayoutStore from "@/store/zustand/layoutStore";
import usePlanInfoStore from "@/store/zustand/planInfoStore";
import { useProjectCreateStore } from "@/store/zustand/projectCreateStore";
import useSupabaseConnectStore from "@/store/zustand/supabaseConnectStore";
import { track } from "@/utils/events/tracker";
import { getAxiosErorrMessage, getUnexpiredToken } from "@/utils/utils";
import { CircleIcon, SquareIcon, TriangleRightIcon } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { useUserId } from "@/utils/utils";
import { onProjectsSnapshot } from "@/store/supabaseSlice";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";

const HomePage = () => {
  const { user } = useUser();
  const navigate = useNavigate();

  const { session } = useSession();

  const [searchParams, setSearchParams] = useSearchParams();
  const componentId = searchParams.get("componentId");
  const invitation = searchParams.get("invitation");
  const prompt = searchParams.get("prompt");

  const dispatch = useDispatch();

  const projectsLoadStatus = useSelector(
    (state: any) => state.supabaseStore.projectsLoadStatus
  );
  const projects = useSelector((state: any) => state.supabaseStore.projects);

  const [importing, setImporting] = useState(false);
  const [acceptingInvitation, setAcceptingInvitation] = useState(false);

  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

  const resetAiActionStore = useAiActionStore(
    (state) => state.resetAiActionStore
  );
  const resetAiContextStore = useAiContextStore(
    (state) => state.resetAiContextStore
  );
  const resetDesignSystemStore = useDesignSystemStore(
    (state) => state.resetDesignSystemStore
  );
  const resetCanvasStore = useCanvasStore((state) => state.resetCanvasStore);
  const resetDevServerStore = useDevServerStore(
    (state) => state.resetDevServerStore
  );
  const resetCodebaseStore = useCodebaseStore(
    (state) => state.resetCodebaseStore
  );
  const resetLayoutStore = useLayoutStore((state) => state.resetLayoutStore);
  const resetSupabaseConnectStore = useSupabaseConnectStore(
    (state) => state.resetSupabaseConnectStore
  );

  const resetProjectCreateFlow = useProjectCreateStore(
    (state) => state.resetProjectCreateFlow
  );

  const setGenerateText = useProjectCreateStore(
    (state: any) => state.setGenerateText
  );
  const setAutoTriggerGenerate = useProjectCreateStore(
    (state: any) => state.setAutoTriggerGenerate
  );

  const showUpgradeModal = usePlanInfoStore((state) => state.showUpgradeModal);
  const setShowUpgradeModal = usePlanInfoStore(
    (state) => state.setShowUpgradeModal
  );

  const firstTimeNoProjects = useMemo(() => {
    return (
      projectsLoadStatus === "succeeded" &&
      Object.keys(projects || {}).length === 0
    );
  }, [projectsLoadStatus, projects]);

  const { triggerRefetchProjects } = useSupabase();

  const userId = useUserId();

  /**
   * When the user goes back to the home page, reset the editor state
   */
  useEffect(() => {
    resetLayoutStore();
    dispatch(resetCodebaseState());
    dispatch(resetGithubState());
    resetAiActionStore();
    resetAiContextStore();
    resetDesignSystemStore();
    resetCanvasStore();
    resetCodebaseStore();
    resetSupabaseConnectStore();
    resetDevServerStore();
  }, []);

  /**
   * This effect accepts an invitation to a project.
   * It is triggered when the user opens an invitation link.
   */
  useEffect(() => {
    if (!userId) {
      return;
    }

    if (!supabaseClient) {
      return;
    }

    if (!invitation) {
      return;
    }

    if (typeof invitation !== "string") {
      return;
    }

    const acceptInvitation = async () => {
      const supabaseJWT = await getUnexpiredToken(session);

      axios
        .post(
          `${import.meta.env.VITE_BACKEND_BASE_URL}/invitations/accept`,
          {
            token: invitation,
          },
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          }
        )
        .then((res) => {
          triggerRefetchProjects?.();
          const canvasId = res.data?.canvas?.id;
          const projectId = res.data?.project?.id;
          if (canvasId) {
            // TODO: this seems inconsistent
            navigate(`/canvases/${canvasId}/editor`);
          } else if (projectId) {
            // Filter for the invited project
            setSearchParams((prev) => ({
              ...prev,
              project_id: projectId,
            }));
          }
        })
        .catch((error) => {
          toast.warning(
            "Couldn't accept invitation: " + getAxiosErorrMessage(error)
          );
        })
        .finally(() => {
          setAcceptingInvitation(false);
        });
    };

    setAcceptingInvitation(true);

    acceptInvitation();
  }, [invitation, supabaseClient, user]);

  useEffect(() => {
    if (componentId || !session) {
      return;
    }

    if (prompt?.trim()) {
      const openProjectWithPrompt = async () => {
        setGenerateText(prompt?.trim());

        const supabaseJWT = await getUnexpiredToken(session);

        const res = await axios.post(
          `${import.meta.env.VITE_BACKEND_BASE_URL}/projects/add`,
          {
            userId,
            repoPath: "TempoLabsAI/starter-vite-swc",
            repoName: "React SWC Starter",
            framework: "VITE",
            runMode: "CLOUD",
            nodeVersion: "18",
            packageManager: "npm",
          },
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          }
        );

        const { project: createdProject, canvas: createCanvas } = res.data;

        dispatch(
          onProjectsSnapshot({
            newItems: [createdProject],
            deletedItems: [],
          })
        );

        await navigate(`/canvases/${createCanvas.id}/editor?generate-prd=t`);
      };

      setImporting(true);

      openProjectWithPrompt().then(() => {
        setImporting(false);
      });
    }
  }, [prompt, Boolean(session)]);

  /**
   * When the user has no projects, drop them into the first time floe
   */
  useEffect(() => {
    if (!session || !user) {
      return;
    }

    if (firstTimeNoProjects && !prompt?.trim() && userId && !componentId) {
      resetProjectCreateFlow();
      navigate(`/project-create/workflow-select`, {
        replace: true,
      });
    }
  }, [firstTimeNoProjects, Boolean(user), Boolean(session)]);

  /**
   * This effect kicks off the component opening flow.
   */
  useEffect(() => {
    if (!session) {
      return;
    }

    if (!userId) {
      return;
    }

    if (!supabaseClient) {
      return;
    }

    if (!componentId) {
      return;
    }

    if (typeof componentId !== "string") {
      return;
    }

    const componentIdInteger = Number.parseInt(componentId);

    if (Number.isNaN(componentIdInteger)) {
      return;
    }

    const openComponent = async () => {
      const { data, error } = await supabaseClient
        .schema("reactcomponents")
        .from("components")
        .select("id")
        .eq("id", componentIdInteger)
        .single();

      if (error) {
        toast.warning("Couldn't open component because invalid ID");
        setImporting(false);
        return;
      }

      const supabaseJWT = await getUnexpiredToken(session);

      axios
        .post(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/projects/openComponent/${componentId}`,
          {
            userId,
          },
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          }
        )
        .then((res) => {
          // Track if used a template or not
          if (res.data?.project?.templateUserId) {
            track("TRACK_USED_RUNNING_TEMPLATE", {
              used: true,
              repoPath: res.data?.project?.repoPath,
            });
          } else {
            track("TRACK_USED_RUNNING_TEMPLATE", {
              used: false,
              repoPath: res.data?.project?.repoPath,
            });
          }

          const canvasId = res.data?.canvas?.id;
          if (canvasId) {
            const url = `/canvases/${canvasId}/editor?componentId=${componentId}${
              prompt ? `&prompt=${prompt}` : ""
            }`;
            navigate(url);
          }
        })
        .catch((error) => {
          toast.warning(
            "Couldn't import component: " + getAxiosErorrMessage(error)
          );
        })
        .finally(() => {
          setImporting(false);
        });
    };

    setImporting(true);

    toast.info("Importing component");

    openComponent();
  }, [componentId, supabaseClient, Boolean(user), Boolean(session)]);

  /*
   * This effect creates a PICA secret for the current user.
   */
  useEffect(() => {
    if (!session) return;

    const createPicaSecret = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_BACKEND_BASE_URL}/pica/secrets`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${await getUnexpiredToken(session)}`,
              "Content-Type": "application/json",
            },
          }
        );

        const { error } = await response.json();

        if (error) {
          console.error(`Failed to create secret for user: ${userId}:`, error);
        }
      } catch (error) {
        console.error(`Failed to create secret for user: ${userId}:`, error);
      }
    };

    createPicaSecret();
  }, []);

  if (acceptingInvitation) {
    return (
      <div className="flex w-full h-full justify-center mt-40">
        <div className="w-1/3">
          <Card>
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl">Accepting Invitation</CardTitle>
              <CardDescription>
                Adding you to the project you were invited to.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex gap-4">
              <TriangleRightIcon className="w-10 h-10" strokeWidth="1px" />
              <SquareIcon className="w-10 h-10" strokeWidth="1px" />
              <CircleIcon className="w-10 h-10" strokeWidth="1px" />
            </CardContent>
            <CardFooter>
              <div className="h-1.5 w-full bg-gray-100 overflow-hidden">
                <div className="animate-progress w-full h-full bg-gray-900 origin-left-right"></div>
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  if (importing) {
    return (
      <div className="flex w-full h-full justify-center mt-40">
        <div className="w-1/3">
          <Card>
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl">Importing component</CardTitle>
              <CardDescription>
                Building a project to import your component into.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex gap-4">
              <TriangleRightIcon className="w-10 h-10" strokeWidth="1px" />
              <SquareIcon className="w-10 h-10" strokeWidth="1px" />
              <CircleIcon className="w-10 h-10" strokeWidth="1px" />
            </CardContent>
            <CardFooter>
              <div className="h-1.5 w-full bg-gray-100 overflow-hidden">
                <div className="animate-progress w-full h-full bg-gray-900 origin-left-right"></div>
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={cn("flex flex-col h-screen")}>
        <div>
          <AppHeader showProjectDropdown={true} />
        </div>
        <div className="flex flex-col flex-1 justify-center items-center w-full text-md relative min-h-0">
          <ProjectPage />
        </div>
      </div>
    </>
  );
};

export default HomePage;
